import { useState, useEffect, useRef } from 'react';

/* PILOTO-1B (firma #80) — EL RELOJ QUE HACE VERDAD EL VENCIMIENTO EN PANTALLA,
 * SIN DEPENDER DEL RELOJ DEL APARATO.
 *
 * EL HUECO QUE CIERRA, y es un hueco de medio arreglo. `isCodeExpired` compara
 * contra `Date.now()`, así que un código que ya venció se pinta muerto… pero
 * SOLO si algo re-renderiza. Las dos pantallas de profesor leen una vez al
 * montar y se quedan quietas: una pestaña abierta desde las 6 p.m. seguía
 * mostrando a las 8:15 el código en naranja, con «Vence hoy a las 7:40 p.m.» y
 * los botones de copiar vivos — y el profesor repartía por WhatsApp una llave
 * que ya contesta 410. La capa de reloj existía; le faltaba quien la despertara.
 *
 * QUÉ HACE: arma UN temporizador al vencimiento MÁS PRÓXIMO que todavía está por
 * delante, y al dispararse fuerza un render. Ahí `isCodeExpired` vuelve a
 * evaluarse con el reloj nuevo y la tarjeta se pone en «Vencido» sola, sin
 * recargar y sin pedirle nada al servidor.
 *
 * QUÉ NO HACE, a propósito:
 *   · NO sondea. Un `setInterval` de un minuto sería un render por minuto toda
 *     la noche para un evento que ocurre UNA vez y cuya hora exacta ya sabemos.
 *   · NO vuelve a leer del servidor. El dato para decidir ya está en memoria;
 *     una petición aquí sería gastar el cubo del profesor por nada.
 *   · NO toca el estado del grupo. Solo empuja un contador: quien decide sigue
 *     siendo `isCodeExpired`, con su regla de dos capas.
 *
 * El techo de `setTimeout` son 2^31-1 ms (~24.8 días); más allá, el navegador lo
 * dispara de inmediato y entraríamos en un bucle. Se acota.
 */

const TECHO_SETTIMEOUT = 2147483647;

/**
 * @param {number[]} segundosRestantes lo que el SERVIDOR dijo que falta
 *   (`code_expires_in` de cada grupo). Números, no fechas: ver abajo.
 * @returns {number} milisegundos TRANSCURRIDOS desde que llegaron esos números.
 *
 * MIDE TRANSCURRIDO, NO RELOJ DE PARED, y ahí está toda la gracia. Un intervalo
 * (`Date.now() - t0`) es correcto aunque el reloj del aparato esté días
 * adelantado, porque el sesgo se cancela en la resta; una comparación contra una
 * fecha absoluta no lo es. Con esto, la PC de escuela con la pila del BIOS
 * muerta deja de pintarle «Vencido» al profesor sobre un código perfectamente
 * vivo — que era un callejón sin salida, porque los botones de copiar quedaban
 * apagados y regenerar producía otro código igual de «vencido».
 */
export default function useCodeExpiryTick(segundosRestantes) {
  const [, tick] = useState(0);
  const [transcurrido, setTranscurrido] = useState(0);
  const t0 = useRef(Date.now());

  // La lista se serializa para que el efecto no se re-arme en cada render por
  // una referencia de array nueva con el mismo contenido.
  const firma = (segundosRestantes || [])
    .filter(n => typeof n === 'number' && n >= 0)
    .join('|');

  useEffect(() => {
    t0.current = Date.now();
    setTranscurrido(0);
    if (!firma) return undefined;

    // El más próximo que todavía está por delante. Uno solo: los demás ya
    // vencieron o vencerán después, y cuando éste dispare se re-arma el siguiente.
    const proximo = firma.split('|').map(Number).filter(n => n > 0).sort((a, b) => a - b)[0];
    if (proximo === undefined) return undefined;

    const espera = Math.min(proximo * 1000 + 1000, TECHO_SETTIMEOUT);
    const id = setTimeout(() => {
      setTranscurrido(Date.now() - t0.current);
      tick(n => n + 1);
    }, espera);
    return () => clearTimeout(id);
  }, [firma]);

  return transcurrido;
}
